import { useState, useEffect, useCallback } from "react";
import { getDocs, type Query } from "firebase/firestore";

interface UseFirestoreCollectionOnceResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  /** Re-runs the query. Call after a mutation to pick up changes. */
  refetch: () => void;
}

/**
 * One-time Firestore collection fetch (getDocs, not onSnapshot).
 *
 * Per ADR-015: "Reserve onSnapshot-based reactivity for cases where the UI
 * must update without user action while the user is looking at it — which,
 * in this app, is a very small set." The admin portal is single-user and
 * form-driven; list screens refetch on mount and after mutations rather
 * than maintaining long-lived listeners.
 *
 * The returned `refetch` is a stable callback — pass it through to mutation
 * hooks (e.g. `await remove(id); refetch();`) without worrying about stale
 * closures.
 */
export function useFirestoreCollectionOnce<T>(
  query: Query | null,
): UseFirestoreCollectionOnceResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Version counter — incremented by refetch() to force the effect to re-run
  // without relying on query object identity changing.
  const [version, setVersion] = useState(0);
  // Track whether we've completed at least one fetch. After the initial load,
  // refetches update data in-place without flashing a loading spinner — this
  // prevents the full-page spinner from unmounting child components (e.g. an
  // open dialog) during background poll refreshes.
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!query) {
      setData([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (!initialLoadDone) {
      setLoading(true);
    }

    getDocs(query)
      .then((snapshot) => {
        if (cancelled) return;
        setData(
          snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() }) as T,
          ),
        );
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setInitialLoadDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [query, version]);

  return { data, loading, error, refetch };
}
